import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { PATHS } from "@constants/Paths";
import { useAnalytics } from "@piiqtechnologies/ui-lib";
import { ChevronDown, ChevronRight } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekStack, PeekTooltip, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import messages from "../messages.i18n";
import { NavigationTitle } from "./NavigationTitle";
import { NavigationListItem } from "./NavigationListItem";
export const NavigationHistory = ({ reportHistory = [], activeReportId, showSettings = true, onReportClick, }) => {
    var _a, _b, _c, _d, _e, _f;
    const intl = useIntl();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const analytics = useAnalytics();
    const [expandedGroups, setExpandedGroups] = useState({});
    if (!reportHistory.length)
        return null;
    const defaultExpandedGroupId = (_d = (_b = (activeReportId && ((_a = reportHistory.find((g) => g.runs.some((r) => r.id === activeReportId))) === null || _a === void 0 ? void 0 : _a.id))) !== null && _b !== void 0 ? _b : (_c = reportHistory.find((g) => g.runs.length > 0)) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : (_e = reportHistory[0]) === null || _e === void 0 ? void 0 : _e.id;
    const isGroupExpanded = (groupId) => {
        var _a;
        return (_a = expandedGroups[groupId]) !== null && _a !== void 0 ? _a : groupId === defaultExpandedGroupId;
    };
    const handleToggleGroup = (groupId) => {
        setExpandedGroups((prev) => {
            var _a;
            const current = (_a = prev[groupId]) !== null && _a !== void 0 ? _a : groupId === defaultExpandedGroupId;
            return Object.assign(Object.assign({}, prev), { [groupId]: !current });
        });
    };
    const handleReportClick = (reportId, reportLabel) => {
        if (onReportClick) {
            onReportClick(reportId, reportLabel);
            return;
        }
        analytics.track("Community Page to Report Overview Page", {
            reportDate: reportLabel,
            reportId: reportId,
        });
        navigate(`${PATHS.OVERVIEW.replace(":assessmentId", reportId)}`);
    };
    const handleReportSettingsClick = (settingsId) => {
        analytics.track("Community Page to Report Settings", {
            reportId: settingsId,
        });
        const from = location.pathname.startsWith("/community/")
            ? "community"
            : location.pathname.startsWith("/overview/")
                ? "overview"
                : undefined;
        navigate(`${PATHS.REPORT_SETTINGS.replace(":reportId", settingsId)}?subtopicTab=location`, {
            state: from ? { from } : undefined,
        });
    };
    const latestExistingRun = (_f = reportHistory.find((g) => g.runs.length > 0)) === null || _f === void 0 ? void 0 : _f.runs[0];
    return (React.createElement(PeekStack, { gap: 0.5, width: "100%", height: "-webkit-fill-available" }, reportHistory.map((group, index) => {
        const isFirst = index === 0;
        const isExpanded = isGroupExpanded(group.id);
        const hasRuns = group.runs.length > 0;
        return (React.createElement(PeekStack, { key: group.id, gap: 0.5 },
            React.createElement(PeekBox, { onClick: () => handleToggleGroup(group.id), sx: {
                    display: "flex",
                    alignItems: "flex-start",
                    gap: theme.spacing(0.5),
                    px: theme.spacing(1),
                    py: theme.spacing(0.75),
                    borderRadius: theme.spacing(0.5),
                    backgroundColor: isExpanded ? theme.palette.teal[100] : "transparent",
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor: isExpanded ? theme.palette.teal[100] : theme.palette.action.hover,
                    },
                } },
                React.createElement(PeekBox, { sx: { display: "flex", alignItems: "center", height: theme.spacing(3) } }, isExpanded ? React.createElement(ChevronDown, { width: 14, height: 14 }) : React.createElement(ChevronRight, { width: 14, height: 14 })),
                React.createElement(PeekStack, { gap: 0 },
                    React.createElement(PeekTypography, { variant: "body1", fontWeight: theme.typography.fontWeightMedium }, group.templateName
                        ? intl.formatMessage(messages.reportNavigationReportTypeWithName, {
                            number: group.reportNumber,
                            name: group.templateName,
                        })
                        : intl.formatMessage(messages.reportNavigationReportType, { number: group.reportNumber })),
                    group.isPaused && (React.createElement(PeekTypography, { variant: "caption", fontStyle: "italic", color: theme.palette.error.main }, intl.formatMessage(messages.reportNavigationPaused))))),
            isExpanded && (React.createElement(PeekBox, { sx: {
                    ml: theme.spacing(1.5),
                    borderLeft: `2px solid ${theme.palette.grey[100]}`,
                    pl: theme.spacing(1.5),
                    py: theme.spacing(0.5),
                    height: "auto",
                    overflowY: "auto",
                    scrollbarWidth: "auto",
                } },
                React.createElement(PeekStack, null,
                    hasRuns ? (group.runs.map((run, runIndex) => (React.createElement(NavigationListItem, { key: run.id, className: isFirst && runIndex === 0 ? "community-tutorial-latest-report" : undefined, label: run.label, isActive: activeReportId === run.id, onClick: () => handleReportClick(run.id, run.label) })))) : group.nextRunOn ? (React.createElement(PeekTooltip, { title: intl.formatMessage(messages.reportNavigationNotRunYet) },
                        React.createElement(PeekBox, { onClick: () => latestExistingRun && handleReportClick(latestExistingRun.id, latestExistingRun.label), sx: { cursor: latestExistingRun ? "pointer" : "default" } },
                            React.createElement(NavigationListItem, { label: group.nextRunOn, disabled: true, onClick: () => undefined })))) : null,
                    showSettings && !group.isPaused && (React.createElement(React.Fragment, null,
                        React.createElement(NavigationTitle, { content: intl.formatMessage(messages.reportNavigationOther) }),
                        React.createElement(NavigationListItem, { label: intl.formatMessage(messages.reportNavigationSettings), onClick: () => handleReportSettingsClick(group.id) }))))))));
    })));
};
export default NavigationHistory;
